import React, { useState } from "react";
import useSWR from "swr";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { LogoMark } from "@/components/app/Logo";
import { listCatalogs, addCatalog, deleteCatalog } from "@/lib/projectApi";
import { ArrowLeft, Loader2, Plus, Settings2, Trash2 } from "lucide-react";
import { toast } from "sonner";

const KEY_LABELS = {
  project_types: "Project types",
  phases: "Project phases",
  vendor_types: "Vendor / trade categories",
  authorities: "Dubai authorities & NOC issuers",
  drawing_categories: "Drawing categories",
  drawing_statuses: "Drawing statuses",
  permit_statuses: "Permit statuses",
  payment_methods: "Payment methods",
  payment_milestones: "Payment milestones",
  rooms: "Rooms (for snag list)",
};

const ORDER = [
  "project_types", "phases", "vendor_types", "authorities",
  "drawing_categories", "permit_statuses", "payment_methods",
  "payment_milestones", "rooms", "drawing_statuses",
];

export default function AdminCatalogs() {
  const { user } = useAuth();
  const { data, mutate, isLoading } = useSWR("/catalogs", listCatalogs);
  const [inputs, setInputs] = useState({});

  if (user && user.role !== "superadmin") return <Navigate to="/projects" replace />;

  const addItem = async (key) => {
    const value = (inputs[key] || "").trim();
    if (!value) { toast.error("Enter a value"); return; }
    try {
      await addCatalog({ key, value, order: 100 });
      toast.success(`Added "${value}"`);
      setInputs(i => ({ ...i, [key]: "" }));
      mutate();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const removeItem = async (id, value) => {
    if (!window.confirm(`Remove "${value}"? Existing records that use this value keep the label.`)) return;
    try {
      await deleteCatalog(id);
      mutate();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const keys = data ? [...ORDER.filter(k => data[k]), ...Object.keys(data).filter(k => !ORDER.includes(k))] : [];

  return (
    <div className="min-h-screen">
      <header className="sticky top-3 z-40 mx-auto max-w-5xl px-3 sm:px-4 lg:px-6">
        <div className="glass rounded-full px-4 py-2 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 min-w-0">
            <Link to="/projects" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary shrink-0">
              <ArrowLeft className="h-3.5 w-3.5" /> Projects
            </Link>
            <div className="h-4 w-px bg-border/60 mx-1" />
            <div className="flex items-center gap-1.5">
              <Settings2 className="h-3.5 w-3.5 text-primary" />
              <span className="font-heading text-sm font-bold">Admin · Dropdown lists</span>
            </div>
          </div>
          <div className="hidden md:block text-[11px] text-muted-foreground font-mono uppercase tracking-wider">
            Customize every choice list used across the portal
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-6">
        {isLoading || !data ? (
          <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {keys.map(key => (
              <section key={key} className="rounded-2xl border border-border bg-card/80 backdrop-blur-xl p-5 card-glossy">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="font-heading text-sm font-bold uppercase tracking-wider text-foreground">
                    {KEY_LABELS[key] || key}
                  </h2>
                  <Badge variant="outline" className="text-[10px]">{data[key].length}</Badge>
                </div>
                <div className="flex flex-wrap gap-2 mb-3 min-h-[36px]">
                  {data[key].map(item => (
                    <div key={item.id} className="group inline-flex items-center gap-1 rounded-full border border-border bg-muted/40 pl-3 pr-1 py-1 text-xs" data-testid={`catalog-${key}-${item.id}`}>
                      <span>{item.value}</span>
                      <button
                        onClick={() => removeItem(item.id, item.value)}
                        className="p-1 rounded-full opacity-40 group-hover:opacity-100 hover:bg-destructive/10 hover:text-destructive transition"
                        title="Remove"
                        data-testid={`btn-delete-catalog-${item.id}`}
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input
                    value={inputs[key] || ""}
                    onChange={e => setInputs(i => ({ ...i, [key]: e.target.value }))}
                    onKeyDown={e => { if (e.key === "Enter") addItem(key); }}
                    placeholder="Add new…"
                    className="h-9"
                    data-testid={`input-catalog-${key}`}
                  />
                  <Button size="sm" onClick={() => addItem(key)} data-testid={`btn-add-catalog-${key}`}>
                    <Plus className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </section>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
